/* eslint-disable prefer-destructuring */
/* eslint-disable no-restricted-globals */
/* eslint-disable no-unused-vars */
/* eslint-disable class-methods-use-this */
import Color from 'color'
import { isEmpty } from 'lodash'
import MapboxLayerGL from '@eqworks/kepler.gl/dist/layers/mapboxgl-layer'
import GeojsonLayerIcon from '@eqworks/kepler.gl/dist/layers/geojson-layer/geojson-layer-icon'
import { createSelector } from 'reselect'


export const tileVisConfigs = {
  opacity: 'opacity',
  colorRange: 'colorRange',
  url: {
    type: 'function',
    defaultValue: () => '#',
  },
  resolution: {
    type: 'string',
    defaultValue: 'ct',
  },
  minValue: {
    type: 'number',
    defaultValue: -1,
  },
  maxValue: {
    type: 'number',
    defaultValue: -1,
  },
  numSteps: {
    type: 'number',
    defaultValue: 7,
  },
  isChoropleth: {
    type: 'boolean',
    defaultValue: true,
  },
  useFeatureState: {
    type: 'boolean',
    defaultValue: false,
  },
  dataType: {
    type: 'string',
    defaultValue: 'demographic',
  },
  metaData: {
    type: 'array',
    defaultValue: [],
  },
  minMax: {
    type: 'array',
    defaultValue: [],
  },
  blendOffset: {
    type: 'array',
    defaultValue: [],
  },
  blendMode: {
    type: 'string',
    defaultValue: 'normal',
  },
}

class MapboxTileLayer extends MapboxLayerGL {
  constructor(props) {
    super(props)
    this.registerVisConfig(tileVisConfigs)
  }

  get type() {
    return 'tile'
  }

  get visualChannels() {
    return {}
  }

  get layerIcon() {
    return GeojsonLayerIcon
  }

  get requiredLayerColumns() {
    return ['metric']
  }

  getMinMax = (data, columns) => {
    let max = 0
    let min = 0
    data.forEach((row) => {
      const value = row[columns.metric.fieldIdx]
      if (!isNaN(value)) {
        if (value > max) {
          max = value
        } else if ((min === 0 && value > 0) || value < min) {
          min = value
        }
      }
    })
    return { min, max }
  }

  // ranks data categories based on their frequency
  rankCategories = (data, fieldIdx) => {
    const categoryFrequency = data.reduce((accum, record) => {
      accum[record[fieldIdx]] = (accum[record[fieldIdx]] || 0) + 1
      return accum
    }, {})

    const rankedCategories = Object.entries(categoryFrequency)
      .sort((a, b) => b[1] - a[1])
      .map((category) => category[0])
    //  TO-DO: pagination to be able to display all possible categories
    const numOfCategories = rankedCategories.length
    return { rankedCategories, numOfCategories }
  }

  // Modifies hue of selected colour object for each distinct category
  useColorCategorical = (i, colorObjectHsl) => {
    const useColor = Color(`hsl(${colorObjectHsl[0] + (i * 36)}, 70%, ${colorObjectHsl[2]}%)`)
      .rgb().alpha(1)
    return useColor
  }

  // Returns colour of gradient for chloropleth/numeric data based
  useColorNumeric = (i, range, colorObjectHsl, blendMode, blendOffset) => {
    const baseColor = Color(`hsl(${colorObjectHsl[0]}, 70%, ${range[i]}%)`)
      .rgb().alpha(1)
    let useColor = baseColor
    if (blendOffset.length > 0) {
      const offsetColor = Color(`hsl(${Color(blendOffset).hsl().color[0]}, 70%, ${range[i]}%)`)
        .rgb().alpha(1)
      const blendColor = blendMode === 'additive'
        ? baseColor.color.map((c, i) => c + offsetColor.color[i])
        : baseColor.color.map((c, i) => c - offsetColor.color[i])
      useColor = new Color(blendColor)
    }
    return useColor
  }

  // Returns styling (ie colour) conditions for each category or interval
  setCondition = (style, metric, i, useFeatureState, isCategorical) => {
    const operator = isCategorical ? '==' : '<'
    const valueConversion = isCategorical ? 'to-string' : 'to-number'
    const idx = isCategorical ? i : i + 1
    const obtainMetric = useFeatureState ? 'feature-state' : 'get'

    return [operator, [valueConversion, [obtainMetric, metric]], style[idx].data]
  }

  // Returns fill colour & opacity conditions
  fillConditions = (
    { style, colorObject, metric, isCategorical },
    useFeatureState, isChoropleth,
  ) => {
    const fillColor = !isChoropleth ? colorObject.rgb().toString() : [
      'case',
      ...this.buildFeatureFillConditions(style, metric, useFeatureState, isCategorical),
    ]

    const fillOpacityCondition = useFeatureState
      ? ['==', ['feature-state', metric], null]
      : ['<=', ['to-number', ['get', metric]], 0]

    return { fillColor, fillOpacityCondition }
  }

  getBreakdown = (data) => {
    const {
      columns,
      visConfig,
      color,
    } = this.config
    const {
      minValue,
      maxValue,
      numSteps,
      minMax,
      blendOffset,
      blendMode,
      dataType,
    } = visConfig

    const fieldIdx = columns.metric.fieldIdx
    const isCategorical = !['demographic', 'aoi'].includes(dataType)
      && isNaN(parseInt(data[0][fieldIdx]))
    const metric = columns.metric.value
    const colorObject = Color(color)
    const colorObjectHsl = colorObject.hsl().color

    let useMin = minValue
    let useMax = maxValue
    if ([minValue, maxValue].includes(-1) && isEmpty(minMax)) {
      const { min, max } = this.getMinMax(data, columns)
      useMin = min
      useMax = max
    } else if (!isEmpty(minMax)) {
      useMin = minMax[metric][0]
      useMax = minMax[metric][1]
    }

    const { rankedCategories, numOfCategories } = this.rankCategories(data, fieldIdx)
    const categories = isCategorical
      // only display top 10 categories on map & legend
      ? rankedCategories.slice(0, 10)
      : this.calculateChoroplethIntervals(useMin, useMax, numSteps)

    const range = !isCategorical && this.calculateRange(numSteps).reverse()
    const style = categories.map((value, i) => {
      const useColor = isCategorical ? this.useColorCategorical(i, colorObjectHsl)
        : this.useColorNumeric(i, range, colorObjectHsl, blendMode, blendOffset)
      return {
        color: useColor,
        data: value,
      }
    })
    return { style, metric, colorObject, numOfCategories, isCategorical }
  }

  calculateChoroplethIntervals = (min, max, length) => {
    // calculate color breakdown based on min and max
    const d = (max - min) / 5
    // calculate minStep based on max min delta
    let minStep = 1
    while ((max - min) / minStep >= 100) {
      minStep *= 10
    }
    const result = []
    for (let i = 0; i < length; i++) {
      result[i] = minStep * Math.round((min + (i * d)) / minStep)
    }
    return result
  }

  calculateRange = (steps) => {
    const min = 25
    const max = 95
    const range = Math.floor((max - min) / (steps - 1 || 1))
    return [...Array(steps).keys()].reduce((prev, cur) => [...prev, min + (range * cur)], [])
  }

  buildFeatureFillConditions = (style, metric, useFeatureState, isCategorical) => {
    const breakdownConditions = []
    const breakdowns = isNaN(style[0].data) ? style : style.slice(0, -1)
    breakdowns.forEach(({ color }, i) => {
      const condition = this.setCondition(style, metric, i, useFeatureState, isCategorical)
      breakdownConditions.push(condition)
      breakdownConditions.push(color.rgb().string())
    })
    breakdownConditions.push(Color.rgb(255, 255, 255).rgb().string())
    return breakdownConditions
  }

  loadFeatureData = (mapboxMap, data, featureIdKey, valueKey) => {
    const { resolution } = this.config.visConfig
    const { dataId: sourceName } = this.config
    const metric = this.config.columns.metric.value
    data.forEach((record) => {
      const id = record[featureIdKey]
      const value = record[valueKey]
      const source = mapboxMap.getSource(sourceName)
      if (source) {
        const featureIdentifier = {
          source: sourceName,
          sourceLayer: resolution,
          id,
        }
        if (mapboxMap.getFeatureState(featureIdentifier)[metric] !== value) {
          mapboxMap.setFeatureState(featureIdentifier, { [metric]: value })
        }
      }
    })
  }

  datasetSelector = (config) => config.dataId

  columnsSelector = (config) => config.columns

  dataSelector = (config) => config.data

  isVisibleSelector = (config) => config.isVisible

  visConfigSelector = (config) => config.visConfig

  colorSelector = (config) => config.color

  computeTileConfiguration = createSelector(
    this.datasetSelector,
    this.columnsSelector,
    this.dataSelector,
    this.isVisibleSelector,
    this.visConfigSelector,
    this.colorSelector,
    (
      datasetId,
      columns,
      data,
      isVisible,
      visConfig,
      color,
    ) => {
      const {
        opacity,
        url,
        minValue,
        maxValue,
        numSteps,
        resolution,
        isChoropleth,
        useFeatureState,
        minMax,
        blendOffset,
        blendMode,
      } = visConfig
      const scopedUrl = url(resolution)
      // TODO: support categorical data
      const { style, metric, colorObject } = this.getBreakdown(data)
      const layer = {
        _sourceType: 'vector',
        _sourceUrl: scopedUrl,
        source: datasetId,
        'source-layer': resolution,
        type: 'fill',
        id: this.id,
        layout: { visibility: isVisible ? 'visible' : 'none' },
      }

      const { fillColor, fillOpacityCondition } = this
        .fillConditions(this.getBreakdown(data), isChoropleth, useFeatureState)

      layer.paint = {
        'fill-color': fillColor,
        'fill-opacity': [
          'case',
          fillOpacityCondition,
          0,
          opacity,
        ],
      }
      return layer
    },
  )

  formatLayerData(_, allData, filteredIndex, oldLayerData, opt = {}) {
    if (this.config.columns.metric.fieldIdx !== -1) {
      const config = this.computeTileConfiguration({
        ...this.config,
        data: allData,
      })
      config.id = this.id
      return {
        columns: this.config.columns,
        config,
        data: allData,
      }
    }
    return false
  }
}

MapboxTileLayer._typeName = 'tile'

export default MapboxTileLayer
